const EVENT_SCROLLED_CLASS = "is-scrolled";
const EVENT_ACTIVE_CLASS = "is-active";
const EVENT_COMPACT_CLASS = "event-submenu--compact";
const EVENT_MENU_OPEN_CLASS = "is-menu-open";
const EVENT_AT_MAX_WIDTH_CLASS = "event-submenu--at-max-width";
const COMPACT_FIT_BUFFER = 2;
const SCROLLED_LAYOUT_SYNC_DELAY = 280;

const setUpEventSubmenu = () => {
  const submenu = document.querySelector("[data-event-submenu]");
  if (!submenu) {
    return;
  }

  const stickySentinel = document.querySelector("[data-event-submenu-sentinel]") || submenu;
  const innerGroup = submenu.querySelector(".event-submenu__inner");
  const leftGroup = submenu.querySelector(".event-submenu__left");
  const linksGroup = submenu.querySelector("[data-event-submenu-links]");
  const ticketButton = submenu.querySelector(".event-submenu__ticket");
  const toggleButton = submenu.querySelector("[data-event-submenu-toggle]");
  const links = Array.from(submenu.querySelectorAll("[data-event-submenu-link]"));
  const sectionMap = new Map();
  let responsiveSyncFrameA = null;
  let responsiveSyncFrameB = null;
  let responsiveSyncTimeout = null;

  links.forEach((link) => {
    const targetId = (link.getAttribute("href") || "").replace("#", "").trim();
    if (!targetId) {
      return;
    }
    const section = document.getElementById(targetId);
    if (!section) {
      return;
    }
    sectionMap.set(link, section);
  });

  const getHeaderOffset = () => {
    const header = document.querySelector(".site-header");
    if (!header) {
      return 90;
    }
    return Math.round(header.getBoundingClientRect().height);
  };

  const setMenuOpen = (isOpen) => {
    const isCompact = submenu.classList.contains(EVENT_COMPACT_CLASS);
    const shouldOpen = Boolean(isCompact && isOpen);

    submenu.classList.toggle(EVENT_MENU_OPEN_CLASS, shouldOpen);

    if (toggleButton) {
      toggleButton.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
    }

    if (!linksGroup) {
      return;
    }

    if (!isCompact) {
      linksGroup.removeAttribute("aria-hidden");
      return;
    }

    linksGroup.setAttribute("aria-hidden", shouldOpen ? "false" : "true");
  };

  const clearResponsiveSync = () => {
    if (responsiveSyncFrameA !== null) {
      window.cancelAnimationFrame(responsiveSyncFrameA);
      responsiveSyncFrameA = null;
    }

    if (responsiveSyncFrameB !== null) {
      window.cancelAnimationFrame(responsiveSyncFrameB);
      responsiveSyncFrameB = null;
    }

    if (responsiveSyncTimeout !== null) {
      window.clearTimeout(responsiveSyncTimeout);
      responsiveSyncTimeout = null;
    }
  };

  const updateResponsiveMode = () => {
    if (!innerGroup || !linksGroup) {
      return;
    }

    const wasOpen = submenu.classList.contains(EVENT_MENU_OPEN_CLASS);
    submenu.classList.remove(EVENT_COMPACT_CLASS, EVENT_MENU_OPEN_CLASS);
    linksGroup.removeAttribute("aria-hidden");

    if (toggleButton) {
      toggleButton.setAttribute("aria-expanded", "false");
    }

    const innerStyles = window.getComputedStyle(innerGroup);
    const maxWidth = Number.parseFloat(innerStyles.maxWidth || "0") || 0;
    const isAtMaxWidth = maxWidth > 0 && innerGroup.getBoundingClientRect().width >= maxWidth - 1;
    submenu.classList.toggle(EVENT_AT_MAX_WIDTH_CLASS, isAtMaxWidth);

    const leftOverflow = leftGroup ? leftGroup.scrollWidth > leftGroup.clientWidth + COMPACT_FIT_BUFFER : false;
    const needsCompact = leftOverflow;

    submenu.classList.toggle(EVENT_COMPACT_CLASS, needsCompact);
    setMenuOpen(wasOpen);
  };

  const setScrolledState = (isScrolled) => {
    const wasScrolled = submenu.classList.contains(EVENT_SCROLLED_CLASS);
    submenu.classList.toggle(EVENT_SCROLLED_CLASS, isScrolled);
    const stateChanged = wasScrolled !== isScrolled;

    if (!stateChanged) {
      updateResponsiveMode();
      return;
    }

    updateResponsiveMode();

    // Re-sync after CSS transitions so long titles/context width are included.
    clearResponsiveSync();
    responsiveSyncFrameA = window.requestAnimationFrame(() => {
      updateResponsiveMode();
      responsiveSyncFrameB = window.requestAnimationFrame(() => {
        updateResponsiveMode();
      });
    });

    responsiveSyncTimeout = window.setTimeout(() => {
      updateResponsiveMode();
    }, SCROLLED_LAYOUT_SYNC_DELAY);
  };

  const updateScrolledStateFallback = () => {
    const stickyThresholdY = getHeaderOffset() + 1;
    setScrolledState(stickySentinel.getBoundingClientRect().top <= stickyThresholdY);
  };

  const updateActiveLink = () => {
    if (!sectionMap.size) {
      return;
    }

    const markerY = window.scrollY + getHeaderOffset() + submenu.offsetHeight + 80;
    let activeLink = null;

    sectionMap.forEach((section, link) => {
      if (section.offsetTop <= markerY) {
        activeLink = link;
      }
    });

    links.forEach((link) => {
      link.classList.toggle(EVENT_ACTIVE_CLASS, link === activeLink);
    });
  };

  const scrollToSection = (event) => {
    const link = event.currentTarget;
    const section = sectionMap.get(link);
    if (!section) {
      return;
    }

    event.preventDefault();

    if (section.tagName === "DETAILS" && !section.open) {
      section.open = true;
    }

    const targetY = section.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - submenu.offsetHeight - 32;

    window.scrollTo({
      top: Math.max(0, targetY),
      behavior: "smooth",
    });

    if (submenu.classList.contains(EVENT_COMPACT_CLASS)) {
      setMenuOpen(false);
    }
  };

  if (sectionMap.size) {
    links.forEach((link) => {
      link.addEventListener("click", scrollToSection);
    });
  }

  if (toggleButton) {
    toggleButton.addEventListener("click", () => {
      setMenuOpen(!submenu.classList.contains(EVENT_MENU_OPEN_CLASS));
    });
  }

  document.addEventListener("click", (event) => {
    if (!submenu.classList.contains(EVENT_COMPACT_CLASS) || !submenu.classList.contains(EVENT_MENU_OPEN_CLASS)) {
      return;
    }

    if (!submenu.contains(event.target)) {
      setMenuOpen(false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    if (submenu.classList.contains(EVENT_COMPACT_CLASS) && submenu.classList.contains(EVENT_MENU_OPEN_CLASS)) {
      setMenuOpen(false);
    }
  });

  let stickyObserver = null;
  const setUpStickyObserver = () => {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }

    if (!("IntersectionObserver" in window)) {
      updateScrolledStateFallback();
      return;
    }

    stickyObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setScrolledState(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${getHeaderOffset() + 1}px 0px 0px 0px`,
      }
    );

    stickyObserver.observe(stickySentinel);
    updateScrolledStateFallback();
  };

  const onScroll = () => {
    if (!stickyObserver) {
      updateScrolledStateFallback();
    }
    updateActiveLink();
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  let resizeRaf = null;
  window.addEventListener("resize", () => {
    if (resizeRaf !== null) {
      window.cancelAnimationFrame(resizeRaf);
    }

    resizeRaf = window.requestAnimationFrame(() => {
      setUpStickyObserver();
      updateResponsiveMode();
      onScroll();
    });
  });

  setUpStickyObserver();
  updateResponsiveMode();
  onScroll();
};

export default setUpEventSubmenu;
